import gql from 'graphql-tag';
import { contactFields } from './ContactFields';
import { ContactSchema, EnrichContactSchema } from '../../definitions/schemas';
import { ListingConnection, ListingInput } from '../Listing';
import Interface from '../Interface';

interface SearchContactsInput extends ListingInput {
  companyId?: string;
  email?: string;
  limit?: number;
}

interface SearchContactsData {
  contacts: ListingConnection<ContactSchema>;
}

interface SearchCrmContactsData {
  crmContacts: ListingConnection<ContactSchema>;
}

type SearchCrmContactsInput = ListingInput;

interface CreateContactInput {
  companyId?: string | null;
  email?: string;
  enrichment?: string;
  enrichmentLastSync?: string;
  enrichmentProvider?: string;
  firstName?: string | null;
  jobTitle?: string | null;
  lastName?: string | null;
  mobilePhone?: string;
  phoneNumber?: string | null;
}

interface CreateContactData {
  createContact: {
    contact: ContactSchema;
  };
}

interface UpdateContactInput extends CreateContactInput {
  id?: string;
  roomId?: string;
}

interface UpdateContactData {
  updateContact: {
    contact: ContactSchema;
  };
}

interface ResolveContactResult {
  email: string;
  contact: ContactSchema | null;
  created: boolean;
  error: string | null;
}

interface ResolveContactsByEmailData {
  resolveContactsByEmail: {
    results: ResolveContactResult[];
  };
}

interface EnrichContactData {
  enrichContact: EnrichContactSchema;
}

interface ImportCrmContactData {
  crmImportContact: {
    contact: ContactSchema;
  };
}

export default class ContactsInterface extends Interface {
  public async search(variables: SearchContactsInput) {
    const response = await this.query<SearchContactsData>(
      () =>
        this.apolloService.executeQuery<SearchContactsData>({
          query: gql`
          query contacts(
            $companyId: ID
            $email: String
            $limit: Int
            $offset: Int
            $search: String
            $sortBy: ContactSortEnum
            $sortDirection: SortEnum
          ) {
            contacts(
              companyId: $companyId
              email: $email
              limit: $limit
              offset: $offset
              search: $search
              sortBy: $sortBy
              sortDirection: $sortDirection
            ) {
              totalCount
              nodes {
                ${contactFields}
              }
            }
          }
        `,
          variables,
        }),
      'Error searching contacts'
    );

    return response.contacts;
  }

  public async create(input: CreateContactInput, fields = contactFields) {
    return this.mutate<CreateContactData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createContact($input: CreateContactInput!) {
            createContact(input: $input) {
              errors
              contact {
                ${fields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors
          ?.map(({ message }) => message)
          .join(', '),
      };
    }, 'Error creating contact');
  }

  public async update(input: UpdateContactInput, fields = contactFields) {
    return this.mutate<UpdateContactData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateContact($input: UpdateContactInput!) {
            updateContact(input: $input) {
              errors
              contact {
                ${fields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors
          ?.map(({ message }) => message)
          .join(', '),
      };
    }, 'Error updating contact');
  }

  public async resolveByEmails(
    emails: string[],
    fields = contactFields
  ): Promise<ResolveContactsByEmailData> {
    const BATCH_SIZE = 5;

    const chunks: string[][] = [];
    for (let i = 0; i < emails.length; i += BATCH_SIZE) {
      chunks.push(emails.slice(i, i + BATCH_SIZE));
    }

    const batchResults = await Promise.all(
      chunks.map((batch) =>
        this.mutate<ResolveContactsByEmailData>(async () => {
          const response = await this.apolloService.executeMutation({
            mutation: gql`
              mutation resolveContactsByEmail($input: ResolveContactsByEmailInput!) {
                resolveContactsByEmail(input: $input) {
                  results {
                    email
                    created
                    error
                    contact {
                      ${fields}
                    }
                  }
                }
              }
            `,
            variables: { input: { emails: batch } },
          });

          return {
            data: response.data,
            serverErrors: response.errors,
            serverMessage: response.errors
              ?.map(({ message }) => message)
              .join(', '),
          };
        }, 'Error resolving contacts by email')
      )
    );

    const allResults = batchResults.flatMap(
      (data) => data?.resolveContactsByEmail?.results ?? []
    );

    return { resolveContactsByEmail: { results: allResults } };
  }

  public async getEnrichmentData(email: string) {
    const response = await this.query<EnrichContactData>(
      () =>
        this.apolloService.executeQuery<EnrichContactData>({
          query: gql`
            query enrichContact($email: String!) {
              enrichContact(email: $email) {
                avatarUri
                companyId
                companyName
                enrichment
                enrichmentLastSync
                enrichmentProvider
                firstName
                jobTitle
                lastName
                linkedin
                phoneNumber
              }
            }
          `,
          variables: { email },
        }),
      'Error enrich contact'
    );

    return response.enrichContact;
  }

  public async crmSearch(variables: SearchCrmContactsInput) {
    const response = await this.query<SearchCrmContactsData>(
      () =>
        this.apolloService.executeQuery<SearchCrmContactsData>({
          query: gql`
            query crmContacts($search: String!, $limit: Int!, $after: String) {
              crmContacts(search: $search, limit: $limit, after: $after) {
                nextPage
                nodes {
                  id
                  firstName
                  lastName
                  email
                }
              }
            }
          `,
          variables,
        }),
      'Error searching crm contacts',
      true
    );

    return response.crmContacts;
  }

  public async importCrmContact(crmId: string) {
    return this.mutate<ImportCrmContactData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation crmImportContact($input: CrmImportContactInput!) {
            crmImportContact(input: $input) {
              errors
              contact {
                ${contactFields}
              }
            }
          }
        `,
        variables: {
          input: { crmId },
        },
      });

      const { errors, message } = response.data.crmImportContact;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error importing contact');
  }
}
